import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { ServiceProviderPickerValue } from '@wsphere/service-providers/web';
import { Claim } from '@wsphere/warranties/domain';
import moment from 'moment';
import { CLAIM_INTAKE_CONFIG } from '../../config/claim-intake.config';
import { ClaimService } from '../../services';

export interface UsageDimension {
  id: string;
  title: string;

  /** The asset's reading when the warranty was sold. */
  atSale?: number | null;
}

// The picked day is saved as that day in UTC, the day the server compares.
const toFormDate = (date: Date | string | null) => (date ? moment.utc(date).format('YYYY-MM-DD') : null);
const toDate = (value: string | null) => (value ? moment.utc(value).toDate() : null);

const textOrNull = (value: string | null | undefined) => value?.trim() || null;

const EDITABLE_ONCE_AUTHORIZED = ['diagnosis', 'causeOfFailure', 'correction'];

const intakeValidator = (group: AbstractControl): ValidationErrors | null => {
  // Raw, so fields disabled once authorized still count.
  const { lossDate, dateReported, callerName, callerType, usageDimensionId, usageValue } = (
    group as FormGroup
  ).getRawValue();
  const errors: ValidationErrors = {};

  if (lossDate && dateReported && dateReported < lossDate) errors['dateOrder'] = true;
  if (!!textOrNull(callerName) !== !!callerType) errors['callerPair'] = true;
  // A reading can stay picked without a value (it's pre-picked); a value needs to know its reading.
  if (!usageDimensionId && usageValue !== null && usageValue !== '') errors['usagePair'] = true;

  return Object.keys(errors).length ? errors : null;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-intake-dialog',
  templateUrl: './claim-intake.dialog.html',
  standalone: false,
})
export class ClaimIntakeDialog {
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toastSvc: ToastService,
    private claimSvc: ClaimService
  ) {
    // A value typed for one reading doesn't apply to another, so picking a new reading clears it.
    this.form.controls.usageDimensionId.valueChanges.subscribe(() => this.form.controls.usageValue.setValue(null));
  }

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() claim?: Claim.Model | null;
  @Input() usageDimensions: UsageDimension[] = [];

  /** Emits after a successful save. */
  @Output() saved = new EventEmitter<void>();

  readonly config = CLAIM_INTAKE_CONFIG;

  saving = false;
  isLimited = false;
  readonly today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  readonly maxDate = new Date();

  readonly callerTypes = [
    { value: Claim.CallerType.CONTRACT_HOLDER, label: 'Contract holder' },
    { value: Claim.CallerType.SERVICE_PROVIDER, label: 'Service provider' },
    { value: Claim.CallerType.OTHER, label: 'Other' },
  ];

  protected form = this.fb.group(
    {
      lossDate: [null as string | null],
      dateReported: [null as string | null],
      callerName: ['', [Validators.maxLength(Claim.CLAIM_CALLER_NAME_MAX_LENGTH)]],
      callerType: [null as Claim.CallerType | null],
      callerContact: ['', [Validators.maxLength(Claim.CLAIM_CALLER_CONTACT_MAX_LENGTH)]],
      repairOrderReference: ['', [Validators.maxLength(Claim.CLAIM_REPAIR_ORDER_REFERENCE_MAX_LENGTH)]],
      repairDeadline: [null as string | null],
      diagnosis: [''],
      causeOfFailure: [''],
      correction: [''],
      usageDimensionId: [null as string | null],
      usageValue: [null as number | null],
      serviceProvider: [null as ServiceProviderPickerValue | null],
    },
    { validators: intakeValidator }
  );

  open() {
    const claim = this.claim;
    if (!claim) return;

    this.form.reset(
      {
        lossDate: toFormDate(claim.lossDate),
        dateReported: toFormDate(claim.dateReported),
        callerName: claim.caller?.name ?? '',
        callerType: claim.caller?.type ?? null,
        callerContact: claim.caller?.contact ?? '',
        repairOrderReference: claim.repairOrderReference ?? '',
        repairDeadline: toFormDate(claim.repairDeadline),
        diagnosis: claim.diagnosis ?? '',
        causeOfFailure: claim.causeOfFailure ?? '',
        correction: claim.correction ?? '',
        usageDimensionId: claim.usageAtLoss?.dimensionId ?? this.defaultUsageDimensionId(claim),
        usageValue: claim.usageAtLoss?.value ?? null,
        serviceProvider:
          claim.serviceProviderId && claim.serviceProviderSnapshot
            ? { id: claim.serviceProviderId, ...claim.serviceProviderSnapshot }
            : null,
      },
      // Quietly, so filling the form doesn't count as picking a new reading.
      { emitEvent: false }
    );

    // Once authorized, only the findings can change, for the new jobs.
    this.isLimited = !!claim.frozen;
    for (const [key, control] of Object.entries(this.form.controls)) {
      if (this.isLimited && !EDITABLE_ONCE_AUTHORIZED.includes(key)) control.disable({ emitEvent: false });
      else control.enable({ emitEvent: false });
    }
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  /** The reading the term's usage limit is set on, else the only reading there is. */
  private defaultUsageDimensionId(claim: Claim.Model) {
    const warranty = claim.warranty;
    const limited = warranty?.plan?.termUsageLimits?.[warranty.termId]?.dimensionId;
    if (limited && this.usageDimensions.some(dimension => dimension.id === limited)) return limited;

    return this.usageDimensions.length === 1 ? this.usageDimensions[0].id : null;
  }

  toPickerDate(value: string | null | undefined) {
    return value ? moment(value).toDate() : null;
  }

  pickDate(
    control: 'lossDate' | 'dateReported' | 'repairDeadline',
    date: Date | null,
    trigger: MenuTriggerForDirective
  ) {
    this.form.controls[control].setValue(date ? formatDate(date, 'yyyy-MM-dd', 'en') : null);
    trigger.close();
  }

  async submit() {
    const claim = this.claim;
    if (!claim) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const changes = this.getChanges(claim);
    if (!Object.keys(changes).length) return this.close();

    this.saving = true;
    try {
      await this.claimSvc.update(claim.id, changes);

      this.toastSvc.show({
        type: 'success',
        title: 'Claim intake updated',
        text: `<strong>Claim #${claim.claimNumber}</strong> has been successfully updated.`,
      });

      this.close();
      this.saved.emit();
    } catch (err) {
      const message = (err as { error?: { message?: string | string[] } })?.error?.message;
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: (Array.isArray(message) ? message.join(', ') : message) || 'We were unable to update the claim intake.',
      });
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  /** Only the fields that differ from the claim, so the update stays partial. */
  private getChanges(claim: Claim.Model): Claim.UpdateClaimRequest {
    const value = this.form.getRawValue();
    const changes: Claim.UpdateClaimRequest = {};

    if (value.lossDate !== toFormDate(claim.lossDate)) changes.lossDate = toDate(value.lossDate);
    if (value.dateReported !== toFormDate(claim.dateReported)) changes.dateReported = toDate(value.dateReported);
    if (value.repairDeadline !== toFormDate(claim.repairDeadline)) {
      changes.repairDeadline = toDate(value.repairDeadline);
    }

    const callerName = textOrNull(value.callerName);
    const caller =
      callerName && value.callerType
        ? { name: callerName, type: value.callerType, contact: textOrNull(value.callerContact) }
        : null;
    if (
      caller?.name !== claim.caller?.name ||
      caller?.type !== claim.caller?.type ||
      (caller?.contact ?? null) !== (claim.caller?.contact ?? null)
    ) {
      changes.caller = caller;
    }

    const texts = ['repairOrderReference', 'diagnosis', 'causeOfFailure', 'correction'] as const;
    for (const key of texts) {
      const text = textOrNull(value[key]);
      if (text !== (claim[key] ?? null)) changes[key] = text;
    }

    const usage =
      value.usageDimensionId && value.usageValue !== null
        ? { dimensionId: value.usageDimensionId, value: Number(value.usageValue) }
        : null;
    if (usage?.dimensionId !== claim.usageAtLoss?.dimensionId || usage?.value !== claim.usageAtLoss?.value) {
      changes.usageAtLoss = usage;
    }

    const serviceProviderId = value.serviceProvider?.id ?? null;
    if (serviceProviderId !== claim.serviceProviderId) changes.serviceProviderId = serviceProviderId;

    return changes;
  }
}
